/* Anniversary Gift Finder Tool — Core logic
 * Public API: window.AnniversaryFinder.init(rootEl, options)
 *
 * Spec:  tools/ifshe/003-anniversary-gift-finder/design-spec.md
 * Data:  research/anniversary/anniversary-data-sources.md  (LOCKED — do not edit from memory)
 *   Gemstone  = Jewelers of America official list (jewelers.org), IGS-verified
 *   Traditional (US/UK) = Wikipedia "Wedding anniversary" (US vs UK differences captured)
 *   Modern (1937) = American National Retail Jeweler Association list
 * Colours are descriptive gem hues; meanings are traditional symbolism
 * (rendered as "traditionally symbolises", never claimed as fact).
 * 14th = Ivory is historical only — substituted with Gold Jewellery + Opal (never sell ivory).
 */
(function () {
  'use strict';

  // ---------- DATA: anniversary by year (1-25 yearly + 30/35/40/45/50/55/60) ----------
  // tradUS/tradUK differ on some years (US vs UK tradition). modern stored US-spelling,
  // localised at render. gem = JA official; hex = gem hue for the swatch.
  // milestone:true years carry a named jubilee and get the badge + stronger merchandising.
  // Gap years (16-19, 21-24) have null trad/modern (no widely-agreed gift) — gem only.
  var YEARS = [
    { y:1,  tradUS:"Paper",             tradUK:"Cotton",            modern:"Clock",                 gem:"Gold",          color:"Warm gold",        hex:"#d4af37", meaning:"hope and a bright future together" },
    { y:2,  tradUS:"Cotton",            tradUK:"Paper",             modern:"China",                 gem:"Garnet",        color:"Deep red",         hex:"#7b1f2b", meaning:"devotion and enduring trust" },
    { y:3,  tradUS:"Leather",           tradUK:"Leather",           modern:"Crystal / Glass",       gem:"Pearl",         color:"Creamy white",     hex:"#eef0f2", meaning:"purity and quiet strength" },
    { y:4,  tradUS:"Fruit & Flowers",   tradUK:"Linen & Silk",      modern:"Silk / Linen",          gem:"Blue Topaz",    color:"Sky blue",         hex:"#7ec8e3", meaning:"calm, loyalty and clear communication" },
    { y:5,  tradUS:"Wood",              tradUK:"Wood",              modern:"Silverware",            gem:"Sapphire",      color:"Deep blue",        hex:"#1c3f94", meaning:"sincerity and lasting love", milestone:true, jubilee:"Wood" },
    { y:6,  tradUS:"Iron",              tradUK:"Sugar",             modern:"Wood",                  gem:"Amethyst",      color:"Purple / violet",  hex:"#8a5fb0", meaning:"clarity, calm and protection" },
    { y:7,  tradUS:"Wool & Copper",     tradUK:"Wool",              modern:"Desk Set",              gem:"Onyx",          color:"Black",            hex:"#2b2b2b", meaning:"strength and steadfast commitment" },
    { y:8,  tradUS:"Bronze",            tradUK:"Bronze & Salt",     modern:"Linen / Lace",          gem:"Tourmaline",    color:"Pink to green",    hex:"#e06699", meaning:"versatility and renewed energy" },
    { y:9,  tradUS:"Pottery",           tradUK:"Copper",            modern:"Leather",               gem:"Lapis Lazuli",  color:"Deep blue",        hex:"#26619c", meaning:"wisdom, truth and harmony" },
    { y:10, tradUS:"Tin & Aluminum",    tradUK:"Tin",               modern:"Diamond",               gem:"Diamond",       color:"Clear / white",    hex:"#dfe4ea", meaning:"enduring, unbreakable love", milestone:true, jubilee:"Tin" },
    { y:11, tradUS:"Steel",             tradUK:"Steel",             modern:"Fashion Jewelry",       gem:"Turquoise",     color:"Blue-green",       hex:"#30c5b0", meaning:"protection and good fortune" },
    { y:12, tradUS:"Silk",              tradUK:"Silk & Fine Linen", modern:"Pearls / Colored Gems", gem:"Jade",          color:"Green",            hex:"#5cae8e", meaning:"prosperity and serenity" },
    { y:13, tradUS:"Lace",              tradUK:"Lace",              modern:"Textiles / Furs",       gem:"Citrine",       color:"Golden yellow",    hex:"#e6a817", meaning:"warmth, joy and abundance" },
    { y:14, tradUS:"Ivory",             tradUK:"Ivory",             modern:"Gold Jewelry",          gem:"Opal",          color:"Iridescent",       hex:"#bcd2cf", meaning:"hope and many-faceted love", ivorySub:true },
    { y:15, tradUS:"Crystal",           tradUK:"Crystal",           modern:"Watches",               gem:"Ruby",          color:"Vivid red",        hex:"#c41e3a", meaning:"passion and deep, enduring love", milestone:true, jubilee:"Crystal" },
    { y:16, tradUS:null,                tradUK:null,                modern:null,                    gem:"Peridot",       color:"Lime green",       hex:"#9ac23a", meaning:"renewal and good fortune" },
    { y:17, tradUS:null,                tradUK:null,                modern:null,                    gem:"Watches",       color:"Timeless",         hex:"#b8956a", meaning:"a timeless reminder of your years", gemNonStone:true },
    { y:18, tradUS:null,                tradUK:null,                modern:null,                    gem:"Cat's Eye",     color:"Golden chatoyant", hex:"#b6a36e", meaning:"protection and intuition" },
    { y:19, tradUS:null,                tradUK:null,                modern:null,                    gem:"Aquamarine",    color:"Sea blue",         hex:"#86c5d6", meaning:"calm, courage and clarity" },
    { y:20, tradUS:"China / Porcelain", tradUK:"China",             modern:"Platinum",              gem:"Emerald",       color:"Lush green",       hex:"#1f9a6d", meaning:"growth, renewal and prosperity", milestone:true, jubilee:"China" },
    { y:21, tradUS:null,                tradUK:null,                modern:null,                    gem:"Iolite",        color:"Violet-blue",      hex:"#6a5acd", meaning:"vision and inner guidance" },
    { y:22, tradUS:null,                tradUK:null,                modern:null,                    gem:"Spinel",        color:"Red to blue",      hex:"#c0334d", meaning:"vitality and hope" },
    { y:23, tradUS:null,                tradUK:null,                modern:null,                    gem:"Imperial Topaz",color:"Golden orange",    hex:"#e8923a", meaning:"warmth and abundance" },
    { y:24, tradUS:null,                tradUK:null,                modern:null,                    gem:"Tanzanite",     color:"Blue-violet",      hex:"#4a6fc4", meaning:"rare beauty and calm" },
    { y:25, tradUS:"Silver",            tradUK:"Silver",            modern:"Silver",                gem:"Silver",        color:"Bright silver",    hex:"#c0c0c0", meaning:"radiance and 25 years of devotion", milestone:true, jubilee:"Silver Jubilee" },
    { y:30, tradUS:"Pearl",             tradUK:"Pearl",             modern:"Diamond",               gem:"Pearl",         color:"Creamy white",     hex:"#eef0f2", meaning:"purity earned over three decades", milestone:true, jubilee:"Pearl" },
    { y:35, tradUS:"Coral",             tradUK:"Coral",             modern:"Jade",                  gem:"Emerald",       color:"Lush green",       hex:"#1f9a6d", meaning:"deep-rooted, living love" },
    { y:40, tradUS:"Ruby",              tradUK:"Ruby",              modern:"Ruby",                  gem:"Ruby",          color:"Vivid red",        hex:"#c41e3a", meaning:"enduring passion", milestone:true, jubilee:"Ruby" },
    { y:45, tradUS:"Sapphire",          tradUK:"Sapphire",          modern:"Sapphire",              gem:"Sapphire",      color:"Deep blue",        hex:"#1c3f94", meaning:"wisdom and lasting devotion" },
    { y:50, tradUS:"Gold",              tradUK:"Gold",              modern:"Gold",                  gem:"Gold",          color:"Warm gold",        hex:"#d4af37", meaning:"prosperity, endurance and a golden bond", milestone:true, jubilee:"Golden Jubilee" },
    { y:55, tradUS:"Emerald",           tradUK:"Emerald",           modern:"Emerald",               gem:"Alexandrite",   color:"Green to red",     hex:"#6b8e5a", meaning:"transformation and rare endurance" },
    { y:60, tradUS:"Diamond",           tradUK:"Diamond",           modern:"Diamond",               gem:"Diamond",       color:"Clear / white",    hex:"#dfe4ea", meaning:"an unbreakable, brilliant bond", milestone:true, jubilee:"Diamond" }
  ];

  // ---------- helpers ----------
  function ordinal(n) {
    var s = ["th", "st", "nd", "rd"], v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }
  function yearExact(n) {
    for (var i = 0; i < YEARS.length; i++) if (YEARS[i].y === n) return YEARS[i];
    return null;
  }
  // For By Date results that land on a gap year (e.g. 27): nearest year WITH data, <= n preferred.
  function yearNearest(n) {
    var best = null, bestDiff = 1e9;
    for (var i = 0; i < YEARS.length; i++) {
      var diff = Math.abs(YEARS[i].y - n);
      if (diff < bestDiff || (diff === bestDiff && YEARS[i].y < n)) { best = YEARS[i]; bestDiff = diff; }
    }
    return best;
  }
  var DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // Feb=29 allowed
  var MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  // ---------- anniversary maths (By Date) ----------
  // Returns which anniversary a couple is on, the upcoming one, and days until it.
  function anniversaryFromDate(wedM, wedD, wedY, today) {
    today = today || new Date();
    today = new Date(today.getFullYear(), today.getMonth(), today.getDate()); // strip time
    var yearsPassed = today.getFullYear() - wedY;
    // this year's anniversary date (clamp Feb-29 to Feb-28 in non-leap years)
    function annivOn(yr) {
      var dim = new Date(yr, wedM, 0).getDate();        // days in that month/year
      var day = Math.min(wedD, dim);
      return new Date(yr, wedM - 1, day);
    }
    var thisYear = annivOn(today.getFullYear());
    var DAY = 86400000;
    if (today.getTime() === thisYear.getTime()) {
      return { current: yearsPassed, upcoming: yearsPassed, daysUntil: 0, isToday: true };
    } else if (today < thisYear) {
      return { current: yearsPassed - 1, upcoming: yearsPassed, daysUntil: Math.round((thisYear - today) / DAY), isToday: false };
    } else {
      var next = annivOn(today.getFullYear() + 1);
      return { current: yearsPassed, upcoming: yearsPassed + 1, daysUntil: Math.round((next - today) / DAY), isToday: false };
    }
  }

  // ---------- GA4 ----------
  function track(event, params) {
    try {
      if (window.gtag) window.gtag('event', event, params || {});
      else if (window.dataLayer) window.dataLayer.push(Object.assign({ event: event }, params || {}));
    } catch (_) {}
  }

  // ---------- UI controller ----------
  function init(root, opts) {
    opts = opts || {};
    var isUK = (opts.locale === 'en-GB');
    var W = {
      jewellery: isUK ? 'jewellery' : 'jewelry',
      Jewellery: isUK ? 'Jewellery' : 'Jewelry'
    };
    function loc(s) {
      if (!s) return s;
      return isUK ? s.replace(/Jewelry/g, 'Jewellery').replace(/Colored/g, 'Coloured').replace(/Aluminum/g, 'Aluminium') : s;
    }
    function tradFor(yo) { return isUK ? yo.tradUK : yo.tradUS; }

    // elements
    var modeBtns = root.querySelectorAll('[data-agf-mode]');
    var panels = root.querySelectorAll('[data-agf-panel]');
    var resultEl = root.querySelector('[data-agf-result]');
    var eyebrowEl = root.querySelector('[data-agf-eyebrow]');
    var badgeEl = root.querySelector('[data-agf-badge]');
    var countdownEl = root.querySelector('[data-agf-countdown]');
    var cardsEl = root.querySelector('[data-agf-cards]');
    var metaEl = root.querySelector('[data-agf-meta]');
    var shopCta = root.querySelector('[data-agf-shop-cta]');
    var shopHeading = root.querySelector('[data-agf-shop-heading]');
    var shopLink = root.querySelector('[data-agf-shop-link]');
    var giftRadios = root.querySelectorAll('[data-agf-gift]');

    var lastResult = null; // {yo, gift}

    // mode switching
    function activateMode(name, fromUser) {
      modeBtns.forEach(function (b) { b.classList.toggle('is-active', b.dataset.agfMode === name); });
      panels.forEach(function (p) { p.classList.toggle('is-active', p.dataset.agfPanel === name); });
      if (fromUser) track('tool_mode_view', { mode: name });
    }
    modeBtns.forEach(function (b) {
      b.addEventListener('click', function () { activateMode(b.dataset.agfMode, true); });
    });

    // render helpers
    function swatch(hex) { return '<span class="agf-swatch" style="background:' + hex + '"></span>'; }
    function cardHtml(label, value, extra) {
      if (!value) value = '<span class="agf-muted">No widely-agreed gift</span>';
      return '<div class="agf-card' + (extra || '') + '"><span class="agf-card-label">' + label + '</span>' +
             '<span class="agf-card-value">' + value + '</span></div>';
    }

    function renderResult(yo, approxFor) {
      lastResult = { yo: yo, gift: currentGift() };
      var trad = tradFor(yo);
      var gemDisplay = yo.gemNonStone ? yo.gem : (swatch(yo.hex) + '<span>' + yo.gem + '</span>');

      // three-system cards
      var html = '';
      html += cardHtml('Traditional', trad, ' agf-card-trad');
      html += cardHtml('Modern', loc(yo.modern), ' agf-card-modern');
      html += '<div class="agf-card agf-card-gem"><span class="agf-card-label">Gemstone (Jewelers of America)</span>' +
              '<span class="agf-card-value agf-gem-value">' + gemDisplay + '</span></div>';
      cardsEl.innerHTML = html;

      // meta line: colour + meaning (+ ivory note)
      var meta = '';
      if (!yo.gemNonStone) meta += '<span class="agf-meta-color">' + (isUK ? 'Colour' : 'Color') + ': ' + yo.color + '</span> ';
      meta += '<span class="agf-meta-meaning"><em>Traditionally ' + (isUK ? 'symbolises' : 'symbolizes') + '</em> ' + yo.meaning + '.</span>';
      if (yo.ivorySub) meta += ' <span class="agf-ivory-note">The historical 14th-year material was ivory — never bought or sold today. We celebrate it with Gold ' + W.Jewellery + ' and the Opal instead.</span>';
      metaEl.innerHTML = meta;

      // milestone badge
      if (yo.milestone) { badgeEl.textContent = '🏆 ' + yo.jubilee; badgeEl.classList.add('is-visible'); }
      else { badgeEl.textContent = ''; badgeEl.classList.remove('is-visible'); }

      // shop CTA — gemstone is the always-present commercial anchor
      showShop(yo);
      updateGiftCta();
      reveal(yo, approxFor);
      track('tool_anniv_found', { mode: currentMode(), year: yo.y, gem: yo.gem, milestone: !!yo.milestone });
    }

    function showShop(yo) {
      if (!shopCta) return;
      shopCta.classList.remove('agf-hidden');
      var gemWord = yo.gemNonStone ? '' : (yo.gem + ' ');
      if (shopHeading) shopHeading.textContent = 'Celebrate ' + ordinal(yo.y) + ' with ' + gemWord + W.jewellery;
      if (shopLink) shopLink.dataset.agfGem = yo.gem;
    }

    function reveal(yo, approxFor) {
      var label = 'Your ' + ordinal(yo.y) + ' anniversary';
      if (approxFor && approxFor !== yo.y) label = 'Around your ' + ordinal(approxFor) + ' anniversary';
      eyebrowEl.textContent = label;
      resultEl.classList.add('is-visible');
      setTimeout(function () { resultEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, 50);
    }

    function currentMode() {
      var active = root.querySelector('[data-agf-mode].is-active');
      return active ? active.dataset.agfMode : 'year';
    }
    function currentGift() {
      var g = root.querySelector('[data-agf-gift]:checked');
      return g ? g.value : 'partner';
    }

    // ----- Mode 1: By Year -----
    var yearSel = root.querySelector('[data-agf-year-select]');
    if (yearSel) {
      yearSel.addEventListener('change', function () {
        var n = parseInt(yearSel.value, 10);
        if (!n) return;
        countdownEl.classList.add('agf-hidden');
        var yo = yearExact(n);
        if (yo) renderResult(yo);
      });
    }

    // ----- Mode 2: By Wedding Date -----
    var dM = root.querySelector('[data-agf-date-month]');
    var dD = root.querySelector('[data-agf-date-day]');
    var dY = root.querySelector('[data-agf-date-year]');
    var dErr = root.querySelector('[data-agf-date-error]');

    function rebuildDays() {
      if (!dM || !dD) return;
      var m = parseInt(dM.value, 10);
      var y = dY ? parseInt(dY.value, 10) : 0;
      // With a year chosen, use that year's real month length (Feb 29 only in leap years)
      var max = m ? (y ? new Date(y, m, 0).getDate() : DAYS_IN_MONTH[m - 1]) : 31;
      var cur = parseInt(dD.value, 10) || 0;
      var html = '<option value="">Day</option>';
      for (var d = 1; d <= max; d++) html += '<option value="' + d + '"' + (d === cur ? ' selected' : '') + '>' + d + '</option>';
      dD.innerHTML = html;
    }
    function buildYears() {
      if (!dY) return;
      var now = new Date().getFullYear();
      var html = '<option value="">Year</option>';
      for (var y = now; y >= now - 75; y--) html += '<option value="' + y + '">' + y + '</option>';
      dY.innerHTML = html;
    }
    function tryDate() {
      if (!dM || !dD || !dY) return;
      var m = parseInt(dM.value, 10), d = parseInt(dD.value, 10), y = parseInt(dY.value, 10);
      if (!m || !d || !y) { if (dErr) dErr.textContent = ''; return; }
      var today = new Date();
      var wedding = new Date(y, m - 1, Math.min(d, DAYS_IN_MONTH[m - 1]));
      if (wedding > today) { if (dErr) dErr.textContent = 'That date is in the future — please check the year.'; return; }
      if (dErr) dErr.textContent = '';
      var info = anniversaryFromDate(m, d, y, today);
      var n = Math.max(info.upcoming, 1);
      var yo = yearExact(n) || yearNearest(n);
      // countdown line
      var cd;
      if (info.isToday && info.current === 0) cd = '🎉 Congratulations — you got married today! Your 1st anniversary gift is below.';
      else if (info.isToday) cd = '🎉 Happy ' + ordinal(info.current) + ' anniversary — that\'s today!';
      else cd = '⏳ ' + info.daysUntil + ' day' + (info.daysUntil === 1 ? '' : 's') + ' until your ' + ordinal(info.upcoming) + ' anniversary';
      countdownEl.textContent = cd;
      countdownEl.classList.remove('agf-hidden');
      renderResult(yo, n);
      track('tool_date_calc', { upcoming_year: info.upcoming, days_until: info.daysUntil });
    }
    if (dM) dM.addEventListener('change', function () { rebuildDays(); tryDate(); });
    if (dD) dD.addEventListener('change', tryDate);
    if (dY) dY.addEventListener('change', function () { rebuildDays(); tryDate(); });

    // ----- gifting filter -----
    giftRadios.forEach(function (r) {
      r.addEventListener('change', function () {
        track('tool_gift_filter', { target: r.value });
        if (lastResult) { /* keep result; gift only affects secondary CTA copy handled in liquid/JS below */ updateGiftCta(); }
      });
    });
    function updateGiftCta() {
      var giftLink = root.querySelector('[data-agf-gift-link]');
      if (!giftLink) return;
      var g = currentGift();
      var map = giftLink.dataset; // data-agf-href-partner / -him / -her + labels via liquid
      var href = map['agfHref' + g.charAt(0).toUpperCase() + g.slice(1)];
      var label = map['agfLabel' + g.charAt(0).toUpperCase() + g.slice(1)];
      if (href) giftLink.setAttribute('href', href);
      if (label) giftLink.textContent = label;
    }

    // CTA tracking
    if (shopLink) shopLink.addEventListener('click', function () {
      track('tool_shop_cta_click', { gem: shopLink.dataset.agfGem || '', collection: shopLink.dataset.agfCollection || '', layer: 'main' });
    });
    var giftLink = root.querySelector('[data-agf-gift-link]');
    if (giftLink) giftLink.addEventListener('click', function () {
      track('tool_shop_cta_click', { gem: '', collection: giftLink.dataset.agfCollection || '', layer: 'gift' });
    });
    var chartEl = root.querySelector('[data-agf-chart]');
    if (chartEl) chartEl.addEventListener('toggle', function () { if (chartEl.open) track('tool_chart_expand', {}); });

    // init
    rebuildDays();
    buildYears();
    activateMode('year', false); // By Year is hero on both sites
  }

  window.AnniversaryFinder = {
    init: init, YEARS: YEARS,
    ordinal: ordinal, anniversaryFromDate: anniversaryFromDate, yearExact: yearExact, yearNearest: yearNearest
  };
})();
